import React, { useState, useEffect } from 'react';
import {
    BedDouble,
    Search,
    Wrench,
    CheckCircle,
    XCircle,
    Clock,
    Filter
} from 'lucide-react';
import toast from 'react-hot-toast';
import AdminLayout from '../../components/layout/AdminLayout';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { useAuth } from '../../context/AuthContext';
import { roomsAPI, stationsAPI } from '../../services/api';

const RoomStatus = () => {
    const { admin } = useAuth();
    const [rooms, setRooms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [filterCategory, setFilterCategory] = useState('');
    const [selectedStation, setSelectedStation] = useState('');
    const [stations, setStations] = useState([]);
    const [categories, setCategories] = useState([]);

    useEffect(() => {
        if (admin?.role === 'super_admin') {
            fetchStations();
        } else if (admin?.assignedStation) {
            setSelectedStation(admin.assignedStation._id);
        }
    }, [admin]);

    useEffect(() => {
        if (selectedStation) {
            fetchRooms();
        }
    }, [selectedStation, filterStatus]);

    const fetchStations = async () => {
        try {
            const response = await stationsAPI.getAll();
            setStations(response.data.stations);
            if (response.data.stations.length > 0) {
                setSelectedStation(response.data.stations[0]._id);
            }
        } catch (error) {
            console.error('Error fetching stations:', error);
        }
    };

    const fetchRooms = async () => {
        setLoading(true);
        try {
            const params = { station: selectedStation };
            if (filterStatus) params.status = filterStatus;

            const response = await roomsAPI.getAll(params);
            setRooms(response.data.rooms);

            // Extract unique categories
            const uniqueCategories = [...new Set(response.data.rooms.map(r => r.category?.name))].filter(Boolean);
            setCategories(uniqueCategories);
        } catch (error) {
            console.error('Error fetching rooms:', error);
            toast.error('Failed to load rooms');
        } finally {
            setLoading(false);
        }
    };

    const handleStatusChange = async (room, newStatus) => {
        try {
            await roomsAPI.updateStatus(room._id, newStatus);
            toast.success(`Room ${room.roomNumber} marked as ${newStatus}`);
            fetchRooms();
        } catch (error) {
            console.error('Error updating room status:', error);
            toast.error('Failed to update room status');
        }
    };

    const getStatusConfig = (status) => {
        const configs = {
            available: { color: 'bg-green-500', text: 'text-green-700', bg: 'bg-green-50', icon: CheckCircle },
            occupied: { color: 'bg-red-500', text: 'text-red-700', bg: 'bg-red-50', icon: XCircle },
            maintenance: { color: 'bg-yellow-500', text: 'text-yellow-700', bg: 'bg-yellow-50', icon: Wrench },
            reserved: { color: 'bg-blue-500', text: 'text-blue-700', bg: 'bg-blue-50', icon: Clock }
        };
        return configs[status] || configs.available;
    };

    const filteredRooms = rooms.filter((room) => {
        const matchesSearch = room.roomNumber.toLowerCase().includes(search.toLowerCase()) ||
            room.category?.name?.toLowerCase().includes(search.toLowerCase());
        const matchesCategory = !filterCategory || room.category?.name === filterCategory;
        return matchesSearch && matchesCategory;
    });

    // Group rooms by category
    const groupedRooms = filteredRooms.reduce((acc, room) => {
        const category = room.category?.name || 'Uncategorized';
        if (!acc[category]) acc[category] = [];
        acc[category].push(room);
        return acc;
    }, {});

    // Stats
    const stats = {
        total: rooms.length,
        available: rooms.filter(r => r.status === 'available').length,
        occupied: rooms.filter(r => r.status === 'occupied').length,
        maintenance: rooms.filter(r => r.status === 'maintenance').length,
        reserved: rooms.filter(r => r.status === 'reserved').length
    };

    return (
        <AdminLayout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-display font-bold text-midnight-900">
                            Room Status
                        </h1>
                        <p className="text-midnight-600 mt-1">
                            View and manage room availability
                        </p>
                    </div>

                    {/* Station Selector for Super Admin */}
                    {admin?.role === 'super_admin' && (
                        <select
                            value={selectedStation}
                            onChange={(e) => setSelectedStation(e.target.value)}
                            className="input-field max-w-xs"
                        >
                            {stations.map((s) => (
                                <option key={s._id} value={s._id}>
                                    {s.name} ({s.code})
                                </option>
                            ))}
                        </select>
                    )}
                </div>

                {/* Stats Overview */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="bg-midnight-100 rounded-xl p-4 text-center">
                        <p className="text-midnight-500 text-sm">Total</p>
                        <p className="text-2xl font-bold text-midnight-900">{stats.total}</p>
                    </div>
                    <div className="bg-green-100 rounded-xl p-4 text-center">
                        <p className="text-green-600 text-sm">Available</p>
                        <p className="text-2xl font-bold text-green-700">{stats.available}</p>
                    </div>
                    <div className="bg-red-100 rounded-xl p-4 text-center">
                        <p className="text-red-600 text-sm">Occupied</p>
                        <p className="text-2xl font-bold text-red-700">{stats.occupied}</p>
                    </div>
                    <div className="bg-yellow-100 rounded-xl p-4 text-center">
                        <p className="text-yellow-600 text-sm">Maintenance</p>
                        <p className="text-2xl font-bold text-yellow-700">{stats.maintenance}</p>
                    </div>
                    <div className="bg-blue-100 rounded-xl p-4 text-center">
                        <p className="text-blue-600 text-sm">Reserved</p>
                        <p className="text-2xl font-bold text-blue-700">{stats.reserved}</p>
                    </div>
                </div>

                {/* Filters */}
                <div className="flex flex-col sm:flex-row gap-4">
                    <div className="relative flex-1 max-w-md">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-midnight-400" />
                        <input
                            type="text"
                            placeholder="Search rooms..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="input-field pl-12"
                        />
                    </div>
                    <select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        className="input-field max-w-xs"
                    >
                        <option value="">All Status</option>
                        <option value="available">Available</option>
                        <option value="occupied">Occupied</option>
                        <option value="maintenance">Maintenance</option>
                        <option value="reserved">Reserved</option>
                    </select>
                    <select
                        value={filterCategory}
                        onChange={(e) => setFilterCategory(e.target.value)}
                        className="input-field max-w-xs"
                    >
                        <option value="">All Categories</option>
                        {categories.map((cat) => (
                            <option key={cat} value={cat}>{cat}</option>
                        ))}
                    </select>
                </div>

                {/* Room Grid */}
                {loading ? (
                    <div className="flex items-center justify-center py-20">
                        <LoadingSpinner size="lg" />
                    </div>
                ) : filteredRooms.length === 0 ? (
                    <div className="card text-center py-20">
                        <BedDouble className="w-16 h-16 text-midnight-300 mx-auto mb-4" />
                        <h3 className="text-xl font-semibold text-midnight-700">No rooms found</h3>
                        <p className="text-midnight-500 mt-2">Try adjusting your filters</p>
                    </div>
                ) : (
                    <div className="space-y-8">
                        {Object.entries(groupedRooms).map(([category, categoryRooms]) => (
                            <div key={category}>
                                <h2 className="text-lg font-display font-semibold text-midnight-900 mb-4 flex items-center gap-2">
                                    <span className="w-2 h-2 bg-railway-500 rounded-full"></span>
                                    {category}
                                    <span className="text-midnight-400 font-normal text-sm">
                                        ({categoryRooms.length} rooms)
                                    </span>
                                </h2>

                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                                    {categoryRooms.map((room) => {
                                        const statusConfig = getStatusConfig(room.status);
                                        const StatusIcon = statusConfig.icon;

                                        return (
                                            <div
                                                key={room._id}
                                                className={`card p-4 ${statusConfig.bg} border-2 border-transparent hover:border-${room.status === 'available' ? 'green' : room.status === 'occupied' ? 'red' : room.status === 'maintenance' ? 'yellow' : 'blue'}-300 transition-all group`}
                                            >
                                                <div className="flex items-center justify-between mb-3">
                                                    <span className="font-bold text-midnight-900">{room.roomNumber}</span>
                                                    <div className={`w-3 h-3 ${statusConfig.color} rounded-full`}></div>
                                                </div>

                                                <div className="flex items-center gap-1 text-sm mb-3">
                                                    <StatusIcon className={`w-4 h-4 ${statusConfig.text}`} />
                                                    <span className={`${statusConfig.text} capitalize font-medium`}>
                                                        {room.status}
                                                    </span>
                                                </div>

                                                <div className="text-xs text-midnight-500 mb-3">
                                                    Floor: {room.floor || 'Ground'}
                                                </div>

                                                {/* Quick Actions */}
                                                <div className="opacity-0 group-hover:opacity-100 transition-opacity space-y-1">
                                                    {room.status !== 'available' && (
                                                        <button
                                                            onClick={() => handleStatusChange(room, 'available')}
                                                            className="w-full text-xs py-1 bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
                                                        >
                                                            Mark Available
                                                        </button>
                                                    )}
                                                    {room.status !== 'maintenance' && (
                                                        <button
                                                            onClick={() => handleStatusChange(room, 'maintenance')}
                                                            className="w-full text-xs py-1 bg-yellow-500 text-white rounded hover:bg-yellow-600 transition-colors"
                                                        >
                                                            Under Maintenance
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Legend */}
                <div className="card p-4">
                    <h3 className="text-sm font-semibold text-midnight-700 mb-3">Legend</h3>
                    <div className="flex flex-wrap gap-6">
                        <div className="flex items-center gap-2">
                            <div className="w-4 h-4 bg-green-500 rounded"></div>
                            <span className="text-sm text-midnight-600">Available - Ready for booking</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-4 h-4 bg-red-500 rounded"></div>
                            <span className="text-sm text-midnight-600">Occupied - Guest checked in</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-4 h-4 bg-yellow-500 rounded"></div>
                            <span className="text-sm text-midnight-600">Maintenance - Not available</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-4 h-4 bg-blue-500 rounded"></div>
                            <span className="text-sm text-midnight-600">Reserved - Booking approved</span>
                        </div>
                    </div>
                </div>
            </div>
        </AdminLayout>
    );
};

export default RoomStatus;
